/**
 * ConditionGroupEditor — recursive AND/OR condition tree editor.
 * Supports nested groups up to maxDepth levels.
 */
import { useCallback } from 'react';

export interface ConditionRule {
  field: string;
  operator: string;
  value?: any;
  value_field?: string;
}

export interface ConditionGroup {
  logic: string;
  rules: (ConditionRule | ConditionGroup)[];
}

export function isGroup(item: ConditionRule | ConditionGroup): item is ConditionGroup {
  return 'logic' in item && 'rules' in item;
}

interface ConditionGroupEditorProps {
  group: ConditionGroup;
  onChange: (group: ConditionGroup) => void;
  onRemove?: () => void;
  depth?: number;
  maxDepth?: number;
  fields: string[];
  operators: string[];
  inputStyle: React.CSSProperties;
  selectStyle: React.CSSProperties;
  btnStyle: React.CSSProperties;
}

const DEPTH_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#64748B'];
const NO_VALUE_OPS = ['IS_NULL', 'IS_NOT_NULL'];

export function ConditionGroupEditor({
  group, onChange, onRemove,
  depth = 0, maxDepth = 3,
  fields, operators,
  inputStyle, selectStyle, btnStyle,
}: ConditionGroupEditorProps) {
  const borderColor = DEPTH_COLORS[Math.min(depth, DEPTH_COLORS.length - 1)];

  const updateRule = useCallback((idx: number, updated: ConditionRule | ConditionGroup) => {
    const newRules = [...group.rules];
    newRules[idx] = updated;
    onChange({ ...group, rules: newRules });
  }, [group, onChange]);

  const removeRule = useCallback((idx: number) => {
    if (group.rules.length <= 1 && depth === 0) return;
    const newRules = group.rules.filter((_, i) => i !== idx);
    onChange({ ...group, rules: newRules });
  }, [group, onChange, depth]);

  const addCondition = useCallback(() => {
    onChange({
      ...group,
      rules: [...group.rules, { field: 'amount', operator: 'GT', value: 0 }],
    });
  }, [group, onChange]);

  const addGroup = useCallback(() => {
    if (depth >= maxDepth) return;
    onChange({
      ...group,
      rules: [
        ...group.rules,
        { logic: 'AND', rules: [{ field: 'amount', operator: 'GT', value: 0 }] },
      ],
    });
  }, [group, onChange, depth, maxDepth]);

  const setLogic = useCallback((logic: string) => {
    onChange({ ...group, logic });
  }, [group, onChange]);

  return (
    <div style={{
      borderLeft: `2px solid ${borderColor}`,
      paddingLeft: 16,
      marginLeft: depth > 0 ? 0 : 0,
      marginBottom: depth > 0 ? 8 : 0,
      background: depth > 0 ? `rgba(255,255,255,${0.02 * depth})` : 'transparent',
      borderRadius: depth > 0 ? 6 : 0,
      padding: depth > 0 ? '12px 12px 12px 16px' : '0 0 0 16px',
    }}>
      {/* Group header: logic toggle + remove */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {['AND', 'OR'].map(l => (
            <button
              key={l}
              onClick={() => setLogic(l)}
              style={{
                padding: '2px 10px',
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 4,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: group.logic === l ? borderColor : 'var(--border-default)',
                background: group.logic === l ? borderColor : 'transparent',
                color: group.logic === l ? '#fff' : 'var(--text-secondary)',
                transition: 'all 0.15s',
              }}
            >
              {l}
            </button>
          ))}
          {depth > 0 && (
            <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 8 }}>
              Group (depth {depth})
            </span>
          )}
        </div>
        {onRemove && (
          <button
            onClick={onRemove}
            style={{ ...btnStyle, padding: '2px 8px', fontSize: 10, color: 'var(--red)' }}
          >
            Remove Group
          </button>
        )}
      </div>

      {/* Rules */}
      {group.rules.length === 0 && (
        <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, fontStyle: 'italic' }}>
          Add a condition to get started
        </p>
      )}

      {group.rules.map((item, idx) => {
        if (isGroup(item)) {
          return (
            <ConditionGroupEditor
              key={idx}
              group={item}
              onChange={updated => updateRule(idx, updated)}
              onRemove={() => removeRule(idx)}
              depth={depth + 1}
              maxDepth={maxDepth}
              fields={fields}
              operators={operators}
              inputStyle={inputStyle}
              selectStyle={selectStyle}
              btnStyle={btnStyle}
            />
          );
        }

        const cond = item as ConditionRule;
        return (
          <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <select
              value={cond.field || ''}
              onChange={e => updateRule(idx, { ...cond, field: e.target.value })}
              style={{ ...selectStyle, width: 160 }}
            >
              {fields.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select
              value={cond.operator || ''}
              onChange={e => updateRule(idx, { ...cond, operator: e.target.value })}
              style={{ ...selectStyle, width: 140 }}
            >
              {operators.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            {!NO_VALUE_OPS.includes(cond.operator) && (
              <input
                value={cond.value ?? ''}
                onChange={e => {
                  const v = e.target.value;
                  const num = Number(v);
                  updateRule(idx, { ...cond, value: isNaN(num) || v === '' ? v : num });
                }}
                style={{ ...inputStyle, width: 160 }}
                placeholder="value"
              />
            )}
            <button
              onClick={() => removeRule(idx)}
              style={{ ...btnStyle, padding: '3px 8px', color: 'var(--red)' }}
            >
              X
            </button>
          </div>
        );
      })}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={addCondition} style={{ ...btnStyle, fontSize: 11 }}>
          + Add Condition
        </button>
        {depth < maxDepth && (
          <button onClick={addGroup} style={{ ...btnStyle, fontSize: 11 }}>
            + Add Group
          </button>
        )}
      </div>
    </div>
  );
}
